import type { CreateChartPayload } from '@/entities/chart/api/types';
import {
  CHART_GRAPH,
  CHART_TERM_TYPE,
  CHART_TYPE,
} from '@/entities/chart/model/constants';
import type { DashboardDateFilterState } from '@/features/Dashboard/components/DashboardDateFilter';
import { convertDashboardDateFilterUnitToChartTermUnit } from '@/features/Dashboard/components/DashboardDateFilter/utils';
import { pipe } from '@/utils/pipe';

import chartUtils from '../chart-utils';
import { isWordCloudDatasetField } from '../word-cloud-basis';
import * as helper from './helper';

export class PreprocessFactory {
  chart: ChartBaseWithName;
  constructor(chart: ChartBaseWithName) {
    this.chart = chart;
  }

  singleAxisWordCloudPreProcess(
    params: ChartBaseWithName,
  ): ChartBaseWithName | CreateChartPayload {
    if (
      params.type !== CHART_TYPE.SINGLE_AXIS ||
      params.graph !== CHART_GRAPH.WORD_CLOUD ||
      !isWordCloudDatasetField(params.field.type)
    ) {
      return params;
    }

    return {
      ...params,
      field: {
        ...params.field,
        entire: true,
        candidates: [],
      },
    };
  }

  chartSeriesPreProcess(params: ChartBaseWithName): ChartBaseWithName {
    helper.validateChartSeriesLength(params);

    const newSeries = params.series.map((series) =>
      helper.cleanSeriesOptions(series, params),
    );

    const newParams = {
      ...params,
      series: newSeries,
    };

    // CROSS_AXIS TABLE인 경우 다중 시리즈 허용
    if (
      params.type === CHART_TYPE.CROSS_AXIS &&
      params.graph === CHART_GRAPH.TABLE
    ) {
      // 다중 시리즈 허용
      return newParams;
    }

    // COMBO_CHART인 경우 두 개의 시리즈만 허용
    if (params.graph === CHART_GRAPH.COMBO_CHART) {
      return {
        ...params,
        series: newParams.series.slice(0, 2),
      };
    }

    // 다른 모든 경우는 단일 시리즈만 허용
    if (params.series && params.series.length > 1) {
      // 첫 번째 시리즈만 유지
      return {
        ...params,
        series: [newParams.series[0]],
      };
    }

    return newParams;
  }

  runPreprocess(): CreateChartPayload {
    const result = pipe(
      this.chartSeriesPreProcess,
      this.singleAxisWordCloudPreProcess,
    )(this.chart);
    return result;
  }
}

export class ChartDetailPreprocess extends PreprocessFactory {
  normalizeHistorical = (params: ChartBaseWithName): ChartBaseWithName => {
    return {
      ...params,
      historical: helper.getNormalizedHistorical(
        params.historical,
        params.target.term,
      ),
    };
  };

  runPreprocess() {
    return pipe(
      this.normalizeHistorical,
      this.chartSeriesPreProcess,
      this.singleAxisWordCloudPreProcess,
    )(this.chart);
  }
}

export class DashboardPreprocess extends PreprocessFactory {
  historical = false;
  globalFilter: StructuredFilter | null;
  globalDateFilter: DashboardDateFilterState | null;

  constructor({
    chart,
    historical,
    globalFilter,
    globalDateFilter,
  }: {
    chart: ChartBaseWithName;
    historical: boolean;
    globalFilter: StructuredFilter | null;
    globalDateFilter: DashboardDateFilterState | null;
  }) {
    super(chart);
    this.historical = historical;
    this.globalFilter = globalFilter;
    this.globalDateFilter = globalDateFilter;
  }

  overrideGlobalDateFilter = (params: ChartBaseWithName): ChartBaseWithName => {
    if (!this.globalDateFilter) return params;
    if (!this.globalDateFilter.dateRange) return params;
    const termUnit = convertDashboardDateFilterUnitToChartTermUnit(
      this.globalDateFilter.unit,
    );
    return {
      ...params,
      historical: {
        value: params.historical.value,
        frequency: params.historical.value
          ? chartUtils.getFrequencyByTermUnit(termUnit)
          : null,
      },
      target: {
        ...params.target,
        term: {
          type: CHART_TERM_TYPE.FIXED,
          unit: termUnit,
          start_date: this.globalDateFilter.dateRange[0].toJSON(),
          end_date: this.globalDateFilter.dateRange[1].toJSON(),
        },
      },
    };
  };

  overrideGlobalFilter = (params: ChartBaseWithName): ChartBaseWithName => {
    if (this.globalFilter) {
      return {
        ...params,
        target: {
          ...params.target,
          filter: {
            ...params.target.filter,
            ...this.globalFilter,
          },
        },
      };
    }
    return params;
  };

  overrideHistorical = (params: ChartBaseWithName) => {
    return {
      ...params,
      historical: {
        value: this.historical ?? false,
        frequency: this.historical
          ? chartUtils.getFrequencyByTermUnit(this.chart.target.term.unit)
          : null,
      },
    };
  };

  runPreprocess() {
    const dashboardChart = pipe(
      this.overrideHistorical,
      this.overrideGlobalDateFilter,
      this.overrideGlobalFilter,
    )(this.chart);

    return new ChartDetailPreprocess(dashboardChart).runPreprocess();
  }
}
